<template>
  <div class="dataWarehouse">
    <b-navbar type="dark" variant="dark" fixed="top" toggleable="sm">
      <b-navbar-toggle target="nav_collapse"></b-navbar-toggle>
      <b-navbar-brand to="/home" active>🏢</b-navbar-brand>
      <b-collapse is-nav id="nav_collapse">
        <b-navbar-nav>
          <b-nav-item @click="$router.push('/maps')"
            ><b-icon-map></b-icon-map> Map
          </b-nav-item>
          <b-nav-item active
            ><b-icon-file-earmark></b-icon-file-earmark> Data Warehouse
          </b-nav-item>
          <b-nav-item @click="$router.push('/reports')">
            <b-icon-newspaper></b-icon-newspaper> Reports
          </b-nav-item>
        </b-navbar-nav>

        <b-navbar-nav class="ml-auto">
          <b-nav-item @click="logout()">
            <b-icon-box-arrow-left></b-icon-box-arrow-left>
            Logout {{ username }}
            <b-avatar class="mr-3" size="1.3em">
              <v-gravatar :size="100" default-img="retro" :email="username" />
            </b-avatar>
          </b-nav-item>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>

    <main role="main" class="ml-sm-auto px-4">
      <div v-if="!items.length">
        <p class="mt-2">Loading...</p>
        <div class="pb-4">
          <b-overlay show rounded="sm"></b-overlay>
        </div>
      </div>

      <div v-else>
        <b-container fluid>
          <b-row class="mt-3 mb-3">
            <b-input-group>
              <b class="my-1">
                <b-form-datepicker
                  locale="EN"
                  id="startdate-filter"
                  v-model="startdate"
                  :date-format-options="{
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  }"
                  required
                  placeholder="Selected Date"
                ></b-form-datepicker>
              </b>
              <b-input-group-append>
                <b-col class="text-right col-2 my-1">
                  <b-button :disabled="!startdate" @click="startdate = ''"
                    >Clear</b-button
                  >
                </b-col>
              </b-input-group-append>
            </b-input-group>
          </b-row>

          <div class="files">
            <!-- Main table element -->
            <b-link @click="loadFiles" size="sm" class="float-left mb-1"
              ><small>Refresh</small></b-link
            >
            <div class="ml-2 mb-0 float-right text-muted">
              <small>count {{ totalRows }}</small>
            </div>
            <b-table
              :fields="fields"
              :items="items"
              :sort-compare="sortCompare"
              :sort-null-last="true"
              :sort-by="sortBy"
              :sort-desc="true"
              sort-icon-left
              hover
              no-border-collapse
              :filter="startdate"
              :filter-included-fields="filterOn"
              :filter-function="filterFunction"
            >
              <template v-slot:cell(receive_date)="file">
                {{ file.item.receive_date }}
              </template>
              <template v-slot:cell(raw_file)="file">
                <b-link
                  v-if="file.item.raw_debug"
                  size="sm"
                  block
                  class="mb-2"
                  v-b-modal="'info-' + file.item.raw_file"
                  >📦</b-link
                >
                <b-button
                  variant="link"
                  @click="downloadFile(file.item.raw_file, 'raw')"
                  >{{ file.item.raw_file }}</b-button
                >
                <b-modal
                  :id="'info-' + file.item.raw_file"
                  :title="file.item.raw_file"
                  ok-only
                >
                  <pre>{{ file.item.raw_stats }}</pre>
                </b-modal>
              </template>
              <template v-slot:cell(hourly_file)="file">
                <b-link
                  v-if="file.item.hourly_debug"
                  size="sm"
                  block
                  class="mb-2"
                  v-b-modal="'info-' + file.item.hourly_file"
                  >📦</b-link
                >
                <b-button
                  variant="link"
                  @click="downloadFile(file.item.hourly_file, 'hourly')"
                  >{{ file.item.hourly_file }}</b-button
                >
                <b-modal
                  :id="'info-' + file.item.hourly_file"
                  :title="file.item.hourly_file"
                  ok-only
                >
                  <pre>{{ file.item.hourly_stats }}</pre>
                </b-modal>
              </template>
            </b-table>
          </div>
        </b-container>
      </div>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import download from "downloadjs";
import moment from "moment";
export default {
  name: "DataWarehouse",
  components: {},
  data: function() {
    return {
      VUE_APP_API_URL: process.env.VUE_APP_API_URL,
      items: [],
      totalRows: 0,
      sortBy: "receive_date",
      fields: [
        {
          key: "receive_date",
          sortable: true,
        }, //YYYY-MM-DDTHH:mm:ss[Z].
        { key: "raw_file", sortable: true },
        { key: "hourly_file", sortable: true },
      ],
      filterOn: ["receiveDate"],
      startdate: null,
    };
  },
  methods: {
    logout() {
      this.$router.push("/logout");
    },
    setDevExp() {
      this.$store.commit("devexp", !this.debug);
    },
    sortCompare: function(aRow, bRow, key) {
      if (key == "receive_date") {
        const a_receiveDate = aRow.receive_date;
        const b_receiveDate = bRow.receive_date;
        return a_receiveDate.localeCompare(b_receiveDate);
      } else if (key == "raw_file") {
        const a_rawFile = aRow.raw_file;
        const b_rawFile = bRow.raw_file;
        return a_rawFile.localeCompare(b_rawFile);
      } else if (key == "hourly_file") {
        const a_processFile = aRow.hourly_file;
        const b_processFile = bRow.hourly_file;
        return a_processFile.localeCompare(b_processFile);
      }
    },
    filterFunction(item, date) {
      return item && date
        ? item.receive_date.localeCompare(moment(date).format("MM-DD-YYYY")) ==
            0
        : -1;
    },
    loadFiles() {
      this.items = [];
      let promises = [];
      promises.push(
        axios.get(process.env.VUE_APP_API_URL + "/data-store/rawFilesStats", {
          headers: { authorization: localStorage.getItem("density.token") },
        })
      );
      promises.push(
        axios.get(
          process.env.VUE_APP_API_URL + "/data-store/hourlyFilesStats",
          { headers: { authorization: localStorage.getItem("density.token") } }
        )
      );

      axios.all(promises).then((responses) => {
        const rawFiles = { data: responses[0].data, name: "raw" };
        const hourlyFiles = { data: responses[1].data, name: "hourly" };
        const ref =
          rawFiles.data.length > hourlyFiles.data.length
            ? rawFiles
            : hourlyFiles;
        const other =
          rawFiles.data.length <= hourlyFiles.data.length
            ? rawFiles
            : hourlyFiles;
        ref.data.forEach((d) => {
          const found = other.data.find((x) => {
            return (
              x.campus_name === d.campus_name &&
              x.data_date === d.data_date 
            );
          });
          if (ref.name === "raw") {
            this.items.push({
              raw_file: d.filename,
              raw_stats: d.stats,
              raw_debug: true,
              receive_date: d.data_date,
              hourly_file: found ? found.filename : "",
              hourly_stats: found ? found.stats : {},
              hourly_debug: found ? true : false,
            });
          } else {
            this.items.push({
              raw_file: found ? found.filename : "",
              raw_stats: found ? found.stats : {},
              raw_debug: found ? true : false,
              receive_date: found ? found.data_date : "",
              hourly_file: d.filename,
              hourly_stats: d.stats,
              hourly_debug: true,
            });
          }
        });

        this.totalRows = this.items.length;
      });
    },
    downloadFile(fileName, rawOrHourly) {
      var url =
        rawOrHourly == "raw"
          ? process.env.VUE_APP_API_URL +
            "/data-store/rawFile/?filename=" +
            fileName
          : process.env.VUE_APP_API_URL +
            "/data-store/hourlyFile/?filename=" +
            fileName;
      var xmlhttp = new XMLHttpRequest();
      xmlhttp.open("GET", url, true);
      xmlhttp.setRequestHeader(
        "Authorization",
        localStorage.getItem("density.token")
      );
      xmlhttp.responseType = "blob";
      xmlhttp.onload = function(e) {
        download(e.target.response, fileName, "text/plain");
      };
      xmlhttp.send();
    },
  },
  computed: {
    username() {
      return this.$store.state.user.username;
    },
  },
  mounted() {
    if (!this.$store.state.user) {
      this.$router.push("/about");
    }

    this.loadFiles();
    this.dataWarehouse = this.$store.state.dataWarehouse;
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
main {
  margin-bottom: 4.5rem;
  margin-top: 68px;
  text-align: left;
}

h1,
h2,
h3 {
  color: #990000;
}

.navbar.navbar-dark.bg-dark {
  background-color: #990000 !important;
}

.app-icon {
  width: 32px;
  background-color: white;
  padding: 2px;
}
#startdate-filter__outer_ {
  width: 180px;
}
</style>
